import { RemoteTrack, Track } from 'livekit-client';

// Debug instrumentation for the first-turn "lips lead voice" investigation.
// Logs high-frequency WebRTC receiver stats around avatar speech so the
// actual A/V offset — and which side (network, jitter buffer, playout ramp)
// contributes it — can be read off a single console capture.
//
// Everything here is passive: it only reads getStats()/media-element state
// and never influences playback.

// Burst sampling: fast enough to catch the sub-second warm-up dynamics on
// the opening turn, bounded so an idle session doesn't log forever.
const BURST_SAMPLE_INTERVAL_MS = 250;
const BURST_MAX_SAMPLES = 48; // 12s per burst trigger

// audioLevel above this counts as real speech. The server-side pre-roll is
// deterministic dither at ~-72 dBFS (audioLevel ≈ 0.0002), real speech sits
// orders of magnitude higher — so this separates "packets flowing (dither)"
// from "audible speech playing".
const AUDIBLE_LEVEL_THRESHOLD = 0.01;

const LOG_PREFIX = '[AvatarAVSync]';

// The subset of an inbound-rtp stats entry this logger reads. All fields are
// optional — browsers ship different subsets.
type InboundRtpSnapshot = {
  packetsReceived?: number;
  packetsLost?: number;
  jitter?: number;
  jitterBufferDelay?: number;
  jitterBufferEmittedCount?: number;
  jitterBufferTargetDelay?: number;
  jitterBufferMinimumDelay?: number;
  totalSamplesReceived?: number;
  concealedSamples?: number;
  silentConcealedSamples?: number;
  concealmentEvents?: number;
  insertedSamplesForDeceleration?: number;
  removedSamplesForAcceleration?: number;
  audioLevel?: number;
  totalAudioEnergy?: number;
  framesReceived?: number;
  framesDecoded?: number;
  framesDropped?: number;
  framesPerSecond?: number;
  estimatedPlayoutTimestamp?: number;
  lastPacketReceivedTimestamp?: number;
};

const readInboundRtp = (report: RTCStatsReport | undefined): InboundRtpSnapshot | null => {
  if (!report) return null;
  let result: InboundRtpSnapshot | null = null;
  report.forEach((stat: any) => {
    if (stat.type !== 'inbound-rtp') return;
    result = stat as InboundRtpSnapshot;
  });
  return result;
};

// Current jitter-buffer depth in ms over the samples emitted since the
// previous snapshot (the cumulative average is useless here — it is dominated
// by the opening burst for minutes).
const deltaJitterMs = (
  now: InboundRtpSnapshot,
  prev: InboundRtpSnapshot | null,
  field: 'jitterBufferDelay' | 'jitterBufferTargetDelay',
): number | null => {
  if (!prev) return null;
  const dEmitted = (now.jitterBufferEmittedCount ?? 0) - (prev.jitterBufferEmittedCount ?? 0);
  if (dEmitted <= 0) return null;
  const dDelay = (now[field] ?? 0) - (prev[field] ?? 0);
  return Math.round((dDelay / dEmitted) * 1000);
};

const round = (value: number | null | undefined, decimals = 0): number | null => {
  if (typeof value !== 'number' || Number.isNaN(value)) return null;
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
};

export type AvSyncLogger = {
  onTrackSubscribed: (track: RemoteTrack) => void;
  onTrackUnsubscribed: (track: RemoteTrack) => void;
  // Binds the media element whose rendering milestones (first rendered frame,
  // playing/waiting/stalled) should be logged. Pass null to unbind.
  attachMediaElement: (el: HTMLVideoElement | null) => void;
  // Logs a correlation marker (e.g. avatar_speech_started) and (re)starts a
  // stats burst so the surrounding seconds are densely sampled.
  markEvent: (label: string, data?: Record<string, unknown>) => void;
  cleanup: () => void;
};

const createAvSyncLogger = (): AvSyncLogger => {
  const t0 = performance.now();
  const tracks = new Map<Track.Kind, RemoteTrack>();
  let mediaEl: HTMLVideoElement | null = null;
  let removeMediaElListeners: (() => void) | null = null;
  let burstTimer: ReturnType<typeof setInterval> | null = null;
  let burstSamplesLeft = 0;
  let sampleSeq = 0;
  let isCleanedUp = false;

  // Previous stats per kind, for delta computations across samples.
  let prevAudio: InboundRtpSnapshot | null = null;
  let prevVideo: InboundRtpSnapshot | null = null;

  // One-shot milestones — each logged exactly once per session.
  const milestonesSeen = new Set<string>();

  const log = (event: string, data: Record<string, unknown> = {}) => {
    // eslint-disable-next-line no-console
    console.info(LOG_PREFIX, event, {
      elapsedMs: Math.round(performance.now() - t0),
      epochMs: Date.now(),
      ...data,
    });
  };

  const milestone = (name: string, data: Record<string, unknown> = {}) => {
    if (milestonesSeen.has(name)) return;
    milestonesSeen.add(name);
    log(`milestone:${name}`, data);
  };

  const checkAudioMilestones = (audio: InboundRtpSnapshot) => {
    if ((audio.packetsReceived ?? 0) > 0) {
      milestone('audio_first_packet', { packetsReceived: audio.packetsReceived });
    }
    if ((audio.totalSamplesReceived ?? 0) > 0) {
      milestone('audio_first_samples', { totalSamplesReceived: audio.totalSamplesReceived });
    }
    if ((audio.audioLevel ?? 0) >= AUDIBLE_LEVEL_THRESHOLD) {
      milestone('audio_first_audible', {
        audioLevel: round(audio.audioLevel, 4),
        totalSamplesReceived: audio.totalSamplesReceived,
        concealedSamples: audio.concealedSamples,
      });
    }
  };

  const checkVideoMilestones = (video: InboundRtpSnapshot) => {
    if ((video.framesDecoded ?? 0) > 0) {
      milestone('video_first_frame_decoded', { framesDecoded: video.framesDecoded });
    }
  };

  const sampleOnce = async () => {
    const audioTrack = tracks.get(Track.Kind.Audio);
    const videoTrack = tracks.get(Track.Kind.Video);
    if (!audioTrack && !videoTrack) return;

    const [audio, video] = await Promise.all([
      audioTrack?.getRTCStatsReport().then(readInboundRtp) ?? Promise.resolve(null),
      videoTrack?.getRTCStatsReport().then(readInboundRtp) ?? Promise.resolve(null),
    ]);
    if (isCleanedUp) return;

    sampleSeq += 1;
    const entry: Record<string, unknown> = { seq: sampleSeq };

    if (audio) {
      checkAudioMilestones(audio);
      entry.audio = {
        packetsReceived: audio.packetsReceived,
        packetsLost: audio.packetsLost,
        totalSamplesReceived: audio.totalSamplesReceived,
        concealedSamples: audio.concealedSamples,
        silentConcealedSamples: audio.silentConcealedSamples,
        concealmentEvents: audio.concealmentEvents,
        insertedSamplesForDeceleration: audio.insertedSamplesForDeceleration,
        removedSamplesForAcceleration: audio.removedSamplesForAcceleration,
        audioLevel: round(audio.audioLevel, 4),
        totalAudioEnergy: round(audio.totalAudioEnergy, 4),
        jitterBufferCurrentMs: deltaJitterMs(audio, prevAudio, 'jitterBufferDelay'),
        jitterBufferTargetMs: deltaJitterMs(audio, prevAudio, 'jitterBufferTargetDelay'),
        estimatedPlayoutTimestamp: audio.estimatedPlayoutTimestamp,
        lastPacketReceivedTimestamp: audio.lastPacketReceivedTimestamp,
      };
      prevAudio = audio;
    }

    if (video) {
      checkVideoMilestones(video);
      entry.video = {
        packetsReceived: video.packetsReceived,
        framesReceived: video.framesReceived,
        framesDecoded: video.framesDecoded,
        framesDropped: video.framesDropped,
        framesPerSecond: video.framesPerSecond,
        jitterBufferCurrentMs: deltaJitterMs(video, prevVideo, 'jitterBufferDelay'),
        estimatedPlayoutTimestamp: video.estimatedPlayoutTimestamp,
        lastPacketReceivedTimestamp: video.lastPacketReceivedTimestamp,
      };
      prevVideo = video;
    }

    // The headline number: how much older the media currently leaving the
    // audio jitter buffer is than the media leaving the video jitter buffer.
    // Positive = audio lags video = lips lead voice.
    if (
      typeof audio?.estimatedPlayoutTimestamp === 'number'
      && typeof video?.estimatedPlayoutTimestamp === 'number'
    ) {
      entry.avPlayoutSkewMs = Math.round(
        video.estimatedPlayoutTimestamp - audio.estimatedPlayoutTimestamp,
      );
    }

    if (mediaEl) {
      entry.mediaEl = {
        currentTime: round(mediaEl.currentTime, 3),
        readyState: mediaEl.readyState,
        paused: mediaEl.paused,
      };
    }

    log('sample', entry);
  };

  const stopBurst = () => {
    if (burstTimer === null) return;
    clearInterval(burstTimer);
    burstTimer = null;
  };

  const startBurst = (reason: string) => {
    if (isCleanedUp) return;
    burstSamplesLeft = BURST_MAX_SAMPLES;
    if (burstTimer !== null) return; // already running — just extended above
    log('burst_start', { reason });
    burstTimer = setInterval(() => {
      burstSamplesLeft -= 1;
      if (burstSamplesLeft <= 0) {
        stopBurst();
        log('burst_end', {});
      }
      sampleOnce().catch(() => {});
    }, BURST_SAMPLE_INTERVAL_MS);
  };

  const onTrackSubscribed = (track: RemoteTrack) => {
    log('track_subscribed', { kind: track.kind, sid: track.sid, muted: track.isMuted });
    if (track.kind !== Track.Kind.Audio && track.kind !== Track.Kind.Video) return;
    tracks.set(track.kind, track);
    // The session-start window is exactly where the playout path warms up —
    // sample it densely from the moment media can flow.
    startBurst(`track_subscribed:${track.kind}`);
  };

  const onTrackUnsubscribed = (track: RemoteTrack) => {
    if (track.kind !== Track.Kind.Audio && track.kind !== Track.Kind.Video) return;
    if (tracks.get(track.kind) === track) tracks.delete(track.kind);
  };

  const attachMediaElement = (el: HTMLVideoElement | null) => {
    if (removeMediaElListeners) {
      removeMediaElListeners();
      removeMediaElListeners = null;
    }
    mediaEl = el;
    // Guards against non-element stand-ins (tests attach plain objects).
    if (!el || typeof el.addEventListener !== 'function') return;

    const onPlaying = () => log('mediaEl:playing', { currentTime: round(el.currentTime, 3) });
    const onWaiting = () => log('mediaEl:waiting', { currentTime: round(el.currentTime, 3) });
    const onStalled = () => log('mediaEl:stalled', { currentTime: round(el.currentTime, 3) });
    el.addEventListener('playing', onPlaying);
    el.addEventListener('waiting', onWaiting);
    el.addEventListener('stalled', onStalled);
    removeMediaElListeners = () => {
      el.removeEventListener('playing', onPlaying);
      el.removeEventListener('waiting', onWaiting);
      el.removeEventListener('stalled', onStalled);
    };

    // First frame actually composited to the screen — the ground truth for
    // "video started playing", ahead of any RTP-stats approximation.
    const rvfc = (el as any).requestVideoFrameCallback?.bind(el);
    if (rvfc) {
      rvfc((now: number, metadata: any) => {
        milestone('video_first_frame_rendered', {
          mediaTime: round(metadata?.mediaTime, 3),
          presentedFrames: metadata?.presentedFrames,
          receiveTime: round(metadata?.receiveTime, 1),
        });
      });
    }
  };

  const markEvent = (label: string, data: Record<string, unknown> = {}) => {
    log(`mark:${label}`, data);
    // Densely sample around every marker — speech start/end are exactly where
    // the offset is user-visible.
    startBurst(`mark:${label}`);
    // One immediate sample so the marker has stats at t=0, not t=+250ms.
    sampleOnce().catch(() => {});
  };

  const cleanup = () => {
    isCleanedUp = true;
    stopBurst();
    if (removeMediaElListeners) {
      removeMediaElListeners();
      removeMediaElListeners = null;
    }
    mediaEl = null;
    tracks.clear();
  };

  return {
    onTrackSubscribed,
    onTrackUnsubscribed,
    attachMediaElement,
    markEvent,
    cleanup,
  };
};

export default createAvSyncLogger;
